/**
 * App.tsx - Point d'entrée principal de l'application FiverFlow
 * 
 * Cette application utilise une architecture modulaire avec :
 * - Contextes pour la gestion d'état global (Auth, UserData, Loading, Currency)
 * - Routes protégées avec authentification instantanée
 * - Lazy loading pour optimiser les performances
 * - Système de diagnostic pour le développement
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// Contextes pour la gestion d'état global
import { AuthProvider } from './contexts/AuthContext';
import { UserDataProvider } from './contexts/UserDataContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Composants de protection et d'interface
import InstantProtectedRoute from './components/InstantProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AppErrorBoundary from './components/AppErrorBoundary';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import SubscriptionGuard from './components/SubscriptionGuard';
import { GlobalLoadingManager } from './components/GlobalLoadingManager';
import Layout from './components/Layout';
import Error406Diagnostic from './components/Error406Diagnostic';

// Hook pour le préchargement des données
import { usePreloadData } from './hooks/usePreloadData';
// Hook pour synchroniser Stripe à chaque connexion
import { useSyncStripeSubscription } from './hooks/useSyncStripeSubscription';

// Pages principales de l'application
import RootRedirect from './components/RootRedirect';
import AuthSignIn from './pages/AuthSignIn';
import AuthRegister from './pages/AuthRegister';
import AuthCallback from './pages/AuthCallback';
import CreateUsername from './pages/CreateUsername';
import OAuthDebug from './pages/OAuthDebug';
import LandingPage from './pages/LandingPage';
import DashboardExample from './pages/DashboardExample';
import PageCalendar from './pages/PageCalendar';
import PagePricing from './pages/PagePricing';
import WorkboardPage from './pages/TasksPage';
import ProfileRedirect from './pages/ProfileRedirect';
import ProfileUsername from './pages/ProfileUsername';
import PageSettings from './pages/PageSettings';
import PageBilling from './pages/PageBilling';
import ProjectDetailPage from './pages/ProjectDetailPage';
import PageClients from './pages/PageClients';
import PageOrders from './pages/PageOrders';
import TemplatesPage from './pages/TemplatesPage';
import StatsPage from './pages/StatsPage';
import OnboardingPage from './pages/OnboardingPage';
import SuccessPage from './pages/SuccessPage';
import SupportPage from './pages/SupportPage';
import WorkWithUsPage from './pages/WorkWithUsPage';
import AssistantPage from './pages/AssistantPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminStatsPage from './pages/admin/AdminStatsPage';
import AdminAIPage from './pages/admin/AdminAIPage';

// Pages d'abonnement Stripe
import CancelPage from './pages/CancelPage';

// Pages légales
import CookiePolicy from "./components/CookiePolicy";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";

// Pages de documentation
import DocsLayout from './components/DocsLayout';
import DocPage from './pages/docs/DocPage';

// Pages de facturation (lazy loading pour optimiser les performances)
const PageInvoices = lazy(() => import('./pages/PageInvoices'));
const InvoiceTemplatesPage = lazy(() => import('./pages/InvoiceTemplatesPage'));
const InvoiceTemplateEditorPage = lazy(() => import('./pages/InvoiceTemplateEditorPage'));


/**
 * AppContent - Composant principal contenant toutes les routes
 * Utilise le préchargement des données pour optimiser les performances
 */
function AppContent() {
  // 🔥 Préchargement des données en arrière-plan pour une navigation instantanée
  usePreloadData();
  
  // 🔄 Synchronisation automatique de l'abonnement Stripe à chaque connexion
  // Cela garantit que la base de données est toujours synchronisée avec Stripe
  useSyncStripeSubscription();

  return (
    <>
      <Suspense fallback={null}>
        <Routes>
          {/* Redirection racine intelligente */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Landing page - has its own Navbar */}
          <Route path="/home" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          
          {/* Documentation - Sépare complètement du Layout */}
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<DocPage />} />
            <Route path=":page/*" element={<DocPage />} />
          </Route>
          
          {/* Pages publiques */}
          <Route path="/pricing" element={<PagePricing />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/cancel" element={<CancelPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/work-with-us" element={<WorkWithUsPage />} />
          {/* Auth pages */}
          <Route path="/login" element={<AuthSignIn />} />
          <Route path="/register" element={<AuthRegister />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/create-username" element={<CreateUsername />} />
          <Route path="/oauth-debug" element={<OAuthDebug />} />

          {/* Routes protégées avec Layout du dashboard */}
          <Route element={<Layout />}>
            <Route path="/dashboard" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Lunch" pageName="dashboard">
                  <DashboardExample />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />

            {/* Pages internes du dashboard avec protection par abonnement */}
            <Route path="/clients" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Lunch" pageName="clients" description="Gestion des clients (max 5 avec Lunch)">
                  <PageClients />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/orders" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Lunch" pageName="orders" description="Gestion des commandes (max 10 avec Lunch)">
                  <PageOrders />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/calendar" element={
            <InstantProtectedRoute>
              <SubscriptionGuard requiredPlan="Boost" pageName="calendar" description="Calendrier disponible avec Boost">
                <PageCalendar />
              </SubscriptionGuard>
            </InstantProtectedRoute>
          } />
            <Route path="/tasks" element={
            <InstantProtectedRoute>
              <SubscriptionGuard requiredPlan="Boost" pageName="workboard" description="Tableau de travail disponible avec Boost">
                <WorkboardPage />
              </SubscriptionGuard>
            </InstantProtectedRoute>
          } />
            <Route path="/templates" element={<InstantProtectedRoute><TemplatesPage /></InstantProtectedRoute>} />
            <Route path="/stats" element={
            <InstantProtectedRoute>
              <SubscriptionGuard requiredPlan="Scale" pageName="stats" description="Statistiques avancées disponibles avec Scale">
                <StatsPage />
              </SubscriptionGuard>
            </InstantProtectedRoute>
          } />
            {/* Système de profil universel */}
            <Route path="/profile" element={<InstantProtectedRoute><ProfileRedirect /></InstantProtectedRoute>} />
            <Route path="/profiles/:username" element={<InstantProtectedRoute><ProfileUsername /></InstantProtectedRoute>} />
            <Route path="/settings" element={<InstantProtectedRoute><PageSettings /></InstantProtectedRoute>} />
            <Route path="/settings/billing" element={<InstantProtectedRoute><PageBilling /></InstantProtectedRoute>} />
            <Route path="/project/:projectId" element={<InstantProtectedRoute><ProjectDetailPage /></InstantProtectedRoute>} />
            <Route path="/upgrade" element={<InstantProtectedRoute><PagePricing /></InstantProtectedRoute>} />
            <Route path="/success" element={<InstantProtectedRoute><SuccessPage /></InstantProtectedRoute>} />

            {/* Assistant AI */}
            <Route path="/assistant" element={
            <InstantProtectedRoute>
              <SubscriptionGuard requiredPlan="Scale" pageName="assistant" description="AI Assistant available on Scale plan">
                <AssistantPage />
              </SubscriptionGuard>
            </InstantProtectedRoute>
          } />

            {/* Administration - Accès libre pour les admins */}
            <Route path="/admin/dashboard" element={
              <InstantProtectedRoute>
                <AdminRoute><AdminDashboard /></AdminRoute>
              </InstantProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <InstantProtectedRoute>
                <AdminRoute><AdminUsersPage /></AdminRoute>
              </InstantProtectedRoute>
            } />
            <Route path="/admin/stats" element={
              <InstantProtectedRoute>
                <AdminRoute><AdminStatsPage /></AdminRoute>
              </InstantProtectedRoute>
            } />
            <Route path="/admin/ai" element={
              <InstantProtectedRoute>
                <AdminRoute><AdminAIPage /></AdminRoute>
              </InstantProtectedRoute>
            } />

            {/* Système de facturation (lazy loaded) - Scale uniquement */}
            <Route path="/invoices" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Scale" pageName="invoices" description="Système de facturation disponible avec Scale">
                  <PageInvoices />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/invoices/sent" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Scale" pageName="invoices" description="Système de facturation disponible avec Scale">
                  <PageInvoices />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/invoices/create" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Scale" pageName="invoices" description="Système de facturation disponible avec Scale">
                  <PageInvoices />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/invoices/templates" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Scale" pageName="invoices" description="Système de facturation disponible avec Scale">
                  <InvoiceTemplatesPage />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
            <Route path="/invoices/templates/:id" element={
              <InstantProtectedRoute>
                <SubscriptionGuard requiredPlan="Scale" pageName="invoices" description="Système de facturation disponible avec Scale">
                  <InvoiceTemplateEditorPage />
                </SubscriptionGuard>
              </InstantProtectedRoute>
            } />
          </Route>

          {/* Diagnostic Erreur 406 (développement uniquement) */}
          {import.meta.env.DEV && (
            <Route path="/error-406-diagnostic" element={<InstantProtectedRoute><Error406Diagnostic /></InstantProtectedRoute>} />
          )}

          {/* Pages légales */}
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* Onboarding pour nouveaux utilisateurs */}
          <Route path="/onboarding" element={<InstantProtectedRoute><OnboardingPage /></InstantProtectedRoute>} />
        </Routes>
      </Suspense>
    </>
  );
}

/**
 * App - Composant racine de l'application
 * Configure tous les contextes et providers nécessaires
 */
function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <ThemeProvider>
            <Router>
              <AnalyticsWrapper>
                <LoadingProvider>
                  <CurrencyProvider>
                    <UserDataProvider>
                      <GlobalLoadingManager />
                      <AppContent />
                    </UserDataProvider>
                  </CurrencyProvider>
                </LoadingProvider>
              </AnalyticsWrapper>
            </Router>
          </ThemeProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
}


export default App;
